import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Outcome } from './models';
import { baseApi } from '@centerpiece/components';

export const outcome = baseApi
  .enhanceEndpoints({ addTagTypes: ['Outcomes', 'Outcome', 'Project'] })
  .injectEndpoints({
    endpoints: builder => ({
      getOutcomes: builder.query<Outcome[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'outcome',
          method: 'GET',
          params: params ? params : { nested: 'all' },
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Outcome' as const, id })),
                'Outcomes',
              ]
            : ['Outcomes'],
      }),
      getOutcome: builder.query<Outcome, string>({
        query: id => ({
          url: `outcome/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Outcome', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createOutcome: builder.mutation({
        query: request => ({
          url: 'outcome',
          method: 'POST',
          body: request,
        }),
        // invalidatesTags: ['Outcomes'],
        invalidatesTags: (result, error, arg) => [
          'Outcomes',
          { type: 'Project', id: arg.project },
        ],
      }),
      patchOutcome: builder.mutation<Outcome, Outcome>({
        query: request => ({
          url: `outcome/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Outcome', id: arg.id },
        ],
      }),
      updateOutcome: builder.mutation<Outcome, Outcome>({
        query: request => ({
          url: `outcome/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Outcome', id: arg.id },
        ],
      }),
      deleteOutcome: builder.mutation<Outcome, number>({
        query: id => ({
          url: `outcome/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Outcome' as const, arg }, 'Outcomes', 'Project']
            : ['Outcomes'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  usePatchOutcomeMutation,
  useGetOutcomesQuery,
  useGetOutcomeQuery,
  useCreateOutcomeMutation,
  useUpdateOutcomeMutation,
  useDeleteOutcomeMutation,
  useLazyGetOutcomeQuery,
  usePrefetch: useOutcomesPrefetch,
} = outcome;
const selectOutcomesRoot = outcome.endpoints.getOutcomes.select({});
export const selectOutcomes = createSelector(
  selectOutcomesRoot,
  outcome => outcome.data || []
);

export const subscribeOutcome = outcome.endpoints.getOutcome.useQueryState;
